import { Map } from 'ol';
import { Group, Layer } from 'ol/layer';
import ImageLayer from 'ol/layer/Image';
import {
  Dispatch,
  SetStateAction,
  useCallback, useContext, useEffect,
} from 'react';
import { Coordinate } from 'ol/coordinate';
import { DataProvider, Product, ViewMode } from 'types/app.types';
import { App } from 'antd';
import { ImageStatic } from 'ol/source';
import { purgeLayerImages } from 'utils/imageCaches';
import disposeLayerAfterRender from 'utils/layerLifecycle';
import ProductsContext from '../../ProductsContext';
import { getLayerByName, recreateSource } from './utils';
import LayerName from './enums';
import MainAppContext from '../../MainAppContext';
import TimeSeriesContext from '../../TimeSeriesContext';
import { utcDayjs } from '../../../utils';
import zIndexes from '../../../configs/zIndexes';
import { useGeoServerAdapter } from './providers/useGeoServerAdapter';
import { useDTNAdapter } from './providers/useDTNAdapter';
import { LayerManagementAdapter, PopupLayerEntry } from './providers/types';

interface Params {
  layers: string,
  styles: string,
  elevation?: string
}
interface UseProductLayersParams {
  map : Map,
  index: number,
  viewMode: ViewMode,
  setLoadingLayer: Dispatch<SetStateAction<boolean>>,
  preloadLayerFn: (params: {
    index: number;
    product: Product; generatedTimestampList: string[] }) => Promise<void>;
}

function useProductLayers({
  map,
  index,
  setLoadingLayer,
  preloadLayerFn,
} : UseProductLayersParams) {
  const {
    capabilities,
    activeProducts,
    editProductTime,
    getProductById,
  } = useContext(ProductsContext);
  const { message } = App.useApp();
  const { playbackMode, viewMode, currentProjection } = useContext(MainAppContext);
  const {
    play, stop, setRange, setPreloadRange, setPreloadingState, currentPosition,
  } = useContext(TimeSeriesContext);

  const geoserverAdapter = useGeoServerAdapter({
    map, capabilities, currentProjection, message, editProductTime, setLoadingLayer, preloadLayerFn,
  });

  const dtnAdapter = useDTNAdapter({
    map, message, editProductTime, setLoadingLayer, playbackMode, preloadLayerFn,
  });

  const getAdapter = useCallback((provider?: DataProvider): LayerManagementAdapter => (provider === 'dtn' ? dtnAdapter : geoserverAdapter), [dtnAdapter, geoserverAdapter]);

  const addLayer = useCallback(async (product: Product, time: string) => {
    await getAdapter(product.provider).addLayer(product, time);
  }, [getAdapter]);

  const preloadLayer = useCallback(
    (params: {
      index: number;
      product: Product;
      generatedTimestampList: string[]
    }) => getAdapter(params.product.provider).preloadLayer(params),
    [getAdapter],
  );

  const getLayerTooltip = useCallback(
    (
      layer: Layer,
      coordinate: Coordinate | number[],
      destinationProjection: string,
      mapInstance: Map,
    ): Promise<PopupLayerEntry[]> => {
      const provider = layer.get('provider') as DataProvider | undefined;
      return getAdapter(provider)
        .getLayerTooltip(layer, coordinate, destinationProjection, mapInstance);
    },
    [getAdapter],
  );

  const removeLayer = (product: Product, compact = false) => {
    // Notify the adapter so any in-flight async addLayer can be cancelled before it pushes.
    getAdapter(product.provider).notifyRemove(product.id);

    const geoLayer = getLayerByName(map, LayerName.GeoLayer) as Group;

    const layers = geoLayer.getLayers().getArray();
    const curLayer = layers.find((layer) => layer.get('id') === product.id);

    if (curLayer) {
      const layerIndex = curLayer.get('layerIndex');

      // The product is gone: its preloaded frames would only hold memory.
      purgeLayerImages(curLayer);
      geoLayer.getLayers().remove(curLayer);
      disposeLayerAfterRender(map, curLayer as Layer);

      if (compact) {
        const updatedLayers = geoLayer.getLayers().getArray();
        updatedLayers.forEach((layer) => {
          const li = layer.get('layerIndex');
          if (li > layerIndex) {
            layer.set('layerIndex', li - 1);
          }
        });
      }

      const remainingLayers = geoLayer.getLayers().getArray().sort((a, b) => a.get('layerIndex') - b.get('layerIndex'));
      const zIndex = remainingLayers.length;
      remainingLayers.forEach((layer, i) => {
        layer.setZIndex(zIndexes.GEOLAYERS + zIndex - i);
      });
    }
  };

  const updateParam = useCallback(({
    layer, param, value,
  }: {layer: ImageLayer<ImageStatic>, param: keyof Params, value: string }) => {
    try {
      const source = recreateSource({
        layer,
        params: { [param]: value },
        currentProjection,
      });
      layer.setSource(source);
    } catch (error) {
      message.error(`An error occurred while updating layer. ${error}`);
    }
  }, [currentProjection, message]);

  const updateLayerStyle = useCallback((
    productIndex: number,
    styleName: string,
    filterIds?: string[],
  ) => {
    const geoLayer = getLayerByName(map, LayerName.GeoLayer) as Group;
    if (!geoLayer) return;
    const layer = geoLayer.getLayers().getArray().find((l) => l.get('layerIndex') === productIndex);
    if (!layer) return;
    // Frames of the previous style/filter will not be read again (the preload is relaunched).
    const previousStyle = (layer as ImageLayer<ImageStatic>).getSource()?.get('styles') as string | undefined;
    purgeLayerImages(layer, previousStyle);
    const provider = layer.get('provider') as DataProvider | undefined;
    if (provider) {
      getAdapter(provider).updateLayerStyle(productIndex, styleName, filterIds);
      return;
    }
    // GeoServer layers without explicit provider tag
    const imageLayer = layer as ImageLayer<ImageStatic>;
    updateParam({ layer: imageLayer, param: 'styles', value: styleName });
    if (playbackMode) {
      stop();
      setPreloadingState((prev) => prev.map((s) => (s ? 'active' : s)));
      setRange((prev) => [...prev]);
      setPreloadRange((prev) => (prev ? [...prev] : null));
    }
  }, [
    getAdapter,
    map,
    playbackMode,
    setPreloadRange,
    setPreloadingState,
    setRange,
    stop,
    updateParam]);

  const updateLayerOpacity = useCallback((productIndex: number, opacity: number) => {
    const geoLayer = getLayerByName(map, LayerName.GeoLayer) as Group;
    if (geoLayer) {
      const layerIndex = viewMode === 'layer' ? productIndex : 0;
      const item = geoLayer.getLayers().getArray().find((i) => i.get('layerIndex') === layerIndex);
      if (item) { item.setOpacity(opacity / 100); }
    }
  }, [map, viewMode]);

  const updateLayerElevation = useCallback((productIndex: number, elevation: number) => {
    const geoLayer = getLayerByName(map, LayerName.GeoLayer) as Group;
    const product = getProductById(productIndex);
    const activeProductIndex = activeProducts.findIndex((i) => productIndex === i);
    if (geoLayer && product && activeProductIndex !== undefined) {
      const layer = geoLayer.getLayers().getArray().find((l) => l.get('layerIndex') === productIndex) as ImageLayer<ImageStatic>;
      if (layer) {
        updateParam({
          layer, param: 'elevation', value: elevation.toString(),
        });
      }
      if (playbackMode) {
        stop();
        play();
      }
    }
  }, [activeProducts, getProductById, map, play, playbackMode, stop, updateParam]);

  function moveLayer(oldIndex: number, newIndex: number) {
    const geoLayer = getLayerByName(map, LayerName.GeoLayer) as Group;

    const layers = geoLayer.getLayers().getArray();
    const layerToMove = layers.find((l) => l.get('layerIndex') === oldIndex);

    if (layerToMove && oldIndex !== newIndex) {
      layers.forEach((layer) => {
        const currentIndex = layer.get('layerIndex');

        if (oldIndex < newIndex) {
          if (currentIndex > oldIndex && currentIndex <= newIndex) {
            layer.set('layerIndex', currentIndex - 1);
          }
        } else if (oldIndex > newIndex) {
          if (currentIndex >= newIndex && currentIndex < oldIndex) {
            layer.set('layerIndex', currentIndex + 1);
          }
        }
      });

      layerToMove.set('layerIndex', newIndex);

      const sortedLayers = geoLayer
        .getLayers()
        .getArray()
        .sort((a, b) => a.get('layerIndex') - b.get('layerIndex'));

      sortedLayers.forEach((layer, i) => {
        layer.setZIndex(i);
      });

      const zIndex = sortedLayers.length;
      sortedLayers.forEach((layer, i) => {
        layer.setZIndex(zIndexes.GEOLAYERS + zIndex - i);
      });
    }
  }

  const replaceLayer = useCallback((product: Product) => {
    getAdapter(product.provider).replaceLayer(product);
  }, [getAdapter]);

  // Drives layer time updates when the timeline position changes.
  // GeoServer adapter's updateLayerTime is a no-op (handled by useTimeSeriesPlayer).
  useEffect(() => {
    if (!currentPosition) return;
    const geoLayer = getLayerByName(map, LayerName.GeoLayer) as Group;
    if (!geoLayer) return;
    geoLayer.getLayers().getArray().forEach((layer) => {
      const provider = layer.get('provider') as DataProvider | undefined;
      getAdapter(provider).updateLayerTime(layer.get('layerIndex') as number, currentPosition);
    });
  }, [currentPosition, map, getAdapter]);

  useEffect(() => {
    const geoLayer = getLayerByName(map, LayerName.GeoLayer) as Group;
    if (geoLayer?.getLayers().getLength() === 0 && activeProducts.length > 0) {
      if (viewMode === 'layer') {
        activeProducts.forEach((i:number) => {
          const product = getProductById(i);
          if (product) {
            addLayer(product, utcDayjs().toISOString());
          }
        });
      } else {
        activeProducts.forEach((i:number) => {
          if (i === index) {
            const product = getProductById(i);
            if (product) {
              addLayer(product, utcDayjs().toISOString());
            }
          }
        });
      }
    }
  }, [activeProducts, addLayer, getProductById, index, map, viewMode]);

  const playbackTooltipEnabled = activeProducts.some(
    (id) => getAdapter(getProductById(id)?.provider).playbackTooltipEnabled,
  );

  return {
    updateLayerStyle,
    updateLayerOpacity,
    updateLayerElevation,
    addLayer,
    removeLayer,
    moveLayer,
    replaceLayer,
    preloadLayer,
    getLayerTooltip,
    playbackTooltipEnabled,
  };
}

export default useProductLayers;
